import React, { useRef, useState } from "react";
import { Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi, errText, resolveUrl } from "./api";

export const Field = ({ label, icon: Icon, hint, children }) => (
  <label className="block mb-3">
    <span className="flex items-center gap-1.5 text-xs font-bold text-emerald-50/70 uppercase tracking-wide mb-1.5">{Icon && <Icon size={13} />} {label}</span>
    {children}
    {hint && <span className="block text-[11px] text-emerald-50/45 mt-1">{hint}</span>}
  </label>
);

const base = "w-full bg-[#013013] text-white border border-[#1c5f35] rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-[#ffb224] placeholder:text-emerald-50/30 transition-colors";

export const Input = (props) => <input {...props} className={`${base} ${props.className || ""}`} />;
export const Textarea = (props) => <textarea rows={3} {...props} className={`${base} resize-y ${props.className || ""}`} />;
export const Select = (props) => <select {...props} className={`${base} ${props.className || ""}`} />;

export const Btn = ({ variant = "gold", className = "", ...props }) => {
  const styles = {
    gold: "bg-[#ffb224] hover:bg-[#ffc14d] text-[#062a13] border-[#b47400]",
    green: "bg-[#15562e] hover:bg-[#1c6b3a] text-white border-[#0a3a1b]",
    red: "bg-red-600 hover:bg-red-500 text-white border-red-900",
    ghost: "bg-transparent hover:bg-white/5 text-emerald-50/80 border-transparent",
  }[variant];
  return (
    <button
      type="button"
      {...props}
      className={`inline-flex items-center justify-center gap-1.5 font-bold text-sm px-3.5 py-2 rounded-xl border-b-[3px] active:border-b-0 active:translate-y-[3px] disabled:opacity-50 disabled:pointer-events-none transition-all ${styles} ${className}`}
    />
  );
};

export const Card = ({ title, action, children, className = "" }) => (
  <section className={`bg-[#073c1b] border border-[#1c5f35] rounded-2xl p-4 sm:p-5 ${className}`}>
    {(title || action) && (
      <div className="flex items-center justify-between gap-3 mb-4">
        {title && <h2 className="text-lg font-extrabold text-white">{title}</h2>}
        {action}
      </div>
    )}
    {children}
  </section>
);

export const ImagePicker = ({ value, onChange, label = "Image", testId = "image-picker", square = true }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const { url } = await adminApi.upload(file);
      onChange(url);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Field label={label} hint="PNG / WEBP / JPG, max 3 MB. Ya neeche URL paste karein.">
      <div className="flex items-center gap-3">
        <div className={`relative ${square ? "w-20 h-20" : "w-28 h-16"} rounded-xl bg-[#013013] border border-dashed border-[#1c5f35] overflow-hidden flex items-center justify-center shrink-0`}>
          {value ? <img src={resolveUrl(value)} alt="" className="w-full h-full object-cover" /> : <Upload size={20} className="text-emerald-50/40" />}
          {busy && <span className="absolute inset-0 bg-black/60 flex items-center justify-center"><Loader2 size={20} className="animate-spin text-[#ffb224]" /></span>}
        </div>
        <div className="flex-1 min-w-0 flex flex-col gap-2">
          <div className="flex gap-2">
            <Btn data-testid={`${testId}-upload`} variant="green" onClick={() => ref.current?.click()} disabled={busy}><Upload size={15} /> Upload</Btn>
            {value && <Btn data-testid={`${testId}-remove`} variant="ghost" onClick={() => onChange("")}><X size={15} /> Remove</Btn>}
          </div>
          <Input data-testid={`${testId}-url`} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="/images/games/xyz.webp ya https://..." />
        </div>
        <input ref={ref} type="file" accept="image/*" className="hidden" data-testid={`${testId}-file`} onChange={(e) => pick(e.target.files?.[0])} />
      </div>
    </Field>
  );
};

export const ListEditor = ({ value = [], onChange, placeholder, testId }) => (
  <Textarea data-testid={testId} rows={4} value={value.join("\n")} onChange={(e) => onChange(e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))} placeholder={placeholder} />
);
